import { Component, OnInit } from '@angular/core';

import { DynamicDialogConfig, DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';

import { HelperService, HTTPService, DataService, CalculationService } from 'app/services';
import { ConfirmDismissDialogComponent } from 'app/modules/shared/components';
import { itemTypeLabel } from 'app/constants/itemType.constants';
import { orderService } from 'app/constants/estimate.constants';

import { Subscription } from 'rxjs';
import { escapeRegExp, findIndex } from 'lodash';

interface Mitchell1Items {
  labor: [
    {
      level: Array<String>;
      data: [
        {
          name: String;
          note: String;
          operation: String;
          type: String;
          item: {
            application: String;
            note: String;
            skill: ['A', 'B', 'C'];
            laborTime: Number;
            warrTime: String | null;
          };
          level: Array<String>;
          vmrs: {
            workAccomplished: null;
            componentIdentifier: {
              systemLevel: String;
              assemblyLevel: String;
              componentLevel: String;
            };
          };
        }
      ];
    }
  ];
  parts: [
    {
      level: Array<String>;
      data: [
        {
          name: String;
          note: String;
          item: {
            application: String;
            callOut: String;
            graphicId: String;
            note: String;
            partNo: String;
            price: String; // price in US dollar
            priceCdn: String; // price in Canadian dollar
          };
          level: Array<String>;
        }
      ];
    }
  ];
  maintenance: [
    {
      level: Array<String>;
      data: [
        {
          labor: [
            {
              application: String;
              laborTime: Number;
              note: String;
              warrTime: String | null;
              skill: null;
              type: 'Labor';
              levels: Array<String>;
            }
          ];
          notes: [
            {
              heading: String;
              lines: [
                {
                  operation: String;
                  nMvcq: String;
                  name: String;
                  note: String;
                  maintComponents: [];
                }
              ];
              type: 'Notes';
              levels: Array<String>;
            }
          ];
        }
      ];
    }
  ];
  fluids: [
    {
      level: Array<String>;
      data: [
        {
          name: String;
          item: {
            application: String;
            standardVolume: String;
            standardUnit: String;
            metricVolume: String;
            metricUnit: String;
            fluidSpec: String;
            note: String;
            stdHeavy: String;
            nMvcq: String | null;
          };
          type: 'Fluids';
          levels: Array<String>;
          tabId: String;
        }
      ];
    }
  ];
}

@Component({
  selector: 'app-mitchell1-service-assign-dialo',
  templateUrl: './mitchell1-service-assign-dialog.component.html',
  styleUrls: ['./mitchell1-service-assign-dialog.component.scss'],
  providers: [DialogService],
})
export class Mitchell1ServiceAssignDialogComponent implements OnInit {
  isProcessing: boolean = false;
  isSubmitting: boolean = false;
  dataSubscription: Subscription;
  errors: any = {};
  mitchell1ServicesList: any = [];
  serviceList: any = [];
  cols: any = [
    { field: 'title', header: 'Item Type', class: 'file-name' },
    { field: 'category', header: 'Items', class: 'file-type' },
    { field: 'fileType', header: 'Service', class: 'file-format' },
  ];
  services: any[] = [];
  serviceSuggestions: any[] = [];
  items: any[] = [];
  itemTypeLabel = itemTypeLabel;
  mitchell1Tag: any = {};
  mitchell1Settings = {
    parts: {
      pricingMatrix: { pricingId: '', name: '', items: [] },
      displayDescOnOrder: false,
      displayHoursOnOrder: false,
      isTaxable: false,
      commissionable: true,
      qboId: '',
    },
    labor: {
      general: {
        price: 0,
        cost: 0,
        pricingMatrix: { pricingId: '', name: '' },
      },
      maintenance: {
        price: 0,
        cost: 0,
        pricingMatrix: { pricingId: '', name: '' },
      },
      displayDescOnOrder: false,
      displayHoursOnOrder: false,
      isTaxable: false,
      commissionable: true,
      qboId: '',
    },
  };
  itemsDetails: Mitchell1Items;
  serviceItemMap: any = {};
  searchQuery: string = '';
  customFleetSettings: any = {};

  constructor(
    private httpService: HTTPService,
    private helperService: HelperService,
    private dynamicDialogConfig: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private dialogService: DialogService,
    private calculationService: CalculationService
  ) {}

  ngOnInit() {
    const { services, itemsDetails, customFleetSettings } = this.dynamicDialogConfig.data as any;

    if (services && services.length) {
      this.services = this.helperService.deepCopy(services);
      this.serviceSuggestions = this.helperService.deepCopy(services);
    }

    if (customFleetSettings) {
      this.customFleetSettings = customFleetSettings;
    }

    this.itemsDetails = itemsDetails as Mitchell1Items;
    this.getMitchell1Details();
  }

  ngOnDestroy() {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  getMitchell1Details() {
    this.httpService.isMitchell1Connected({ populateItemAndPricing: false, populateMitchell1Refs: true }).then(
      (data) => {
        if (data.settings) {
          // Labor settings
          this.mitchell1Settings.labor.displayDescOnOrder = data.settings.displayDescOnOrderLabor;
          this.mitchell1Settings.labor.displayHoursOnOrder = data.settings.displayHoursOnOrderLabor;
          this.mitchell1Settings.labor.isTaxable = data.settings.isTaxableLabor;
          this.mitchell1Settings.labor.commissionable = data.settings.isCommissionableLabor;

          if (data.settings.qboReferenceLabor && data.settings.qboReferenceLabor.qboId) {
            this.mitchell1Settings.labor.qboId = data.settings.qboReferenceLabor.qboId;
          }

          this.mitchell1Settings.labor.general.cost = data.settings.baseLaborCost;

          if (data.settings.basePricingMatrix && data.settings.basePricingMatrix._id) {
            this.mitchell1Settings.labor.general.price = data.settings.basePricingMatrix.rate;
            this.mitchell1Settings.labor.general.pricingMatrix = {
              pricingId: data.settings.basePricingMatrix._id,
              name: data.settings.basePricingMatrix.name,
            };
          }

          this.mitchell1Settings.labor.maintenance.cost = data.settings.maintainenaceLaborCost;

          if (data.settings.maintainenacePricingMatrix && data.settings.maintainenacePricingMatrix._id) {
            this.mitchell1Settings.labor.maintenance.price = data.settings.maintainenacePricingMatrix.rate;
            this.mitchell1Settings.labor.maintenance.pricingMatrix = {
              pricingId: data.settings.maintainenacePricingMatrix._id,
              name: data.settings.maintainenacePricingMatrix.name,
            };
          }

          // Parts settings
          this.mitchell1Settings.parts.displayDescOnOrder = data.settings.displayDescOnOrderPart;
          this.mitchell1Settings.parts.displayHoursOnOrder = data.settings.displayHoursOnOrderPart;
          this.mitchell1Settings.parts.isTaxable = data.settings.isTaxablePart;
          this.mitchell1Settings.parts.commissionable = data.settings.isCommissionablePart;

          if (data.settings.qboReferencePart && data.settings.qboReferencePart.qboId) {
            this.mitchell1Settings.parts.qboId = data.settings.qboReferencePart.qboId;
          }

          if (data.settings.partPricingMatrix && data.settings.partPricingMatrix._id) {
            this.mitchell1Settings.parts.pricingMatrix = {
              pricingId: data.settings.partPricingMatrix._id,
              name: data.settings.partPricingMatrix.name,
              ...data.settings.partPricingMatrix,
            };
          }
        }

        this.mitchell1Tag = data.mitchell1Tag || {};
        this.mappingItems(this.itemsDetails);
      },
      (err) => {
        this.helperService.showToaster('error', err);
      }
    );
  }

  mappingItems(itemDetails: Mitchell1Items) {
    const items = [];
    let sequence = new Date().getTime();

    if (itemDetails.labor && itemDetails.labor.length) {
      itemDetails.labor.forEach((item) => {
        if (item.data && item.data.length) {
          item.data.forEach((details) => {
            const item: any = {
              _id: 'no-id',
              type: 'labor',
              status: true,
              itemSource: 'Mitchell1',
              itemSourceId: sequence++,
              price: 0,
              cost: 0,
              isTaxable: false,
              displayDescOnOrder: false,
              displayHoursOnOrder: false,
            };

            item.title = details.name;

            if (details.operation) item.title += ` - ${details.operation}`;
            if (details.item.application) item.title += ` (${details.item.application})`;

            item.count = Number(details.item.laborTime);
            item.count = Number(details.item.laborTime);
            item.defaultCount = Number(details.item.laborTime);
            item.actualLaborTime = Number(details.item.laborTime);
            item.billableHours = Number(details.item.laborTime);
            item.costEstimatedHours = Number(details.item.laborTime);
            item.description = details.note;

            if (this.mitchell1Settings.labor) {
              item.price = this.mitchell1Settings.labor.general.price || 0;
              item.cost = this.mitchell1Settings.labor.general.cost || 0;
              item.pricing = this.mitchell1Settings.labor.general.pricingMatrix;
              item.displayDescOnOrder = this.mitchell1Settings.labor.displayDescOnOrder;
              item.displayHoursOnOrder = this.mitchell1Settings.labor.displayHoursOnOrder;
              item.isTaxable = this.mitchell1Settings.labor.isTaxable;
              item.qboId = this.mitchell1Settings.labor.qboId;
              item.commissionable = this.mitchell1Settings.labor.commissionable;
              item.cost = this.calculationService.roundOff(item.cost * item.count);
            }

            this.calculationService.roundOff(item.price * item.count);

            if (this.customFleetSettings) {
              const { isLaborRateOverride, customLaborRate } = this.customFleetSettings;

              if (isLaborRateOverride && customLaborRate) {
                item.pricing = customLaborRate;
                item.price = customLaborRate && customLaborRate.rate;
                item.billRate = customLaborRate && customLaborRate.rate;
              }
            }

            if (this.mitchell1Tag && this.mitchell1Tag._id) {
              item.tags = [
                {
                  name: this.mitchell1Tag.name,
                  color: this.mitchell1Tag.color,
                  tagId: this.mitchell1Tag._id,
                },
              ];
            }

            item.subTotal = this.calculationService.roundOff(item.price * item.count);
            items.push(item);
          });
        }
      });
    }

    if (itemDetails.parts && itemDetails.parts.length) {
      itemDetails.parts.forEach((item) => {
        if (item.data && item.data.length) {
          item.data.forEach((details) => {
            const item: any = {
              _id: 'no-id',
              type: 'part',
              status: true,
              itemSource: 'Mitchell1',
              itemSourceId: sequence++,
              count: 1,
              defaultCount: 1,
              price: 0,
              cost: 0,
              isTaxable: false,
              displayDescOnOrder: false,
              displayHoursOnOrder: false,
            };

            item.title = details.name;

            if (details.item.application) {
              item.title += ` (${details.item.application})`;
            }

            item.description = details.note;
            item.code = details.item.partNo;
            item.price = Number(details.item.price);

            if (this.mitchell1Settings.parts) {
              item.cost = this.calculationService.calculatingMotorPartCost(item.price, this.mitchell1Settings.parts.pricingMatrix);
              item.pricing = this.mitchell1Settings.parts.pricingMatrix;
              item.displayDescOnOrder = this.mitchell1Settings.parts.displayDescOnOrder;
              item.displayHoursOnOrder = this.mitchell1Settings.parts.displayHoursOnOrder;
              item.isTaxable = this.mitchell1Settings.parts.isTaxable;
              item.qboId = this.mitchell1Settings.parts.qboId;
              item.commissionable = this.mitchell1Settings.parts.commissionable;
            }

            if (this.customFleetSettings) {
              const { isPricingMatrixOverride, customPartPricingMatrix } = this.customFleetSettings;

              if (isPricingMatrixOverride && customPartPricingMatrix) {
                item.pricing = { pricingId: customPartPricingMatrix.pricingId, name: customPartPricingMatrix.name };
                item.cost = this.calculationService.calculatingMotorPartCost(item.price, customPartPricingMatrix);
              }
            }

            if (this.mitchell1Tag && this.mitchell1Tag._id) {
              item.tags = [
                {
                  name: this.mitchell1Tag.name,
                  color: this.mitchell1Tag.color,
                  tagId: this.mitchell1Tag._id,
                },
              ];
            }

            item.subTotal = this.calculationService.roundOff(item.price * item.count);
            items.push(item);
          });
        }
      });
    }

    if (itemDetails.maintenance && itemDetails.maintenance.length) {
      itemDetails.maintenance.forEach((data) => {
        data.data.forEach((item) => {
          if (item.labor && item.labor.length) {
            item.labor.forEach((details) => {
              const item: any = {
                _id: 'no-id',
                type: 'labor',
                status: true,
                itemSource: 'Mitchell1',
                itemSourceId: sequence++,
                price: 0,
                cost: 0,
                isTaxable: false,
                displayDescOnOrder: false,
                displayHoursOnOrder: false,
              };

              item.title = `${details.application} ${details.levels && details.levels.join(' ')}`;
              item.count = Number(details.laborTime);
              item.count = Number(details.laborTime);
              item.defaultCount = Number(details.laborTime);
              item.actualLaborTime = Number(details.laborTime);
              item.billableHours = Number(details.laborTime);
              item.costEstimatedHours = Number(details.laborTime);
              item.description = details.note;

              if (this.mitchell1Settings.labor) {
                item.price = this.mitchell1Settings.labor.general.price;
                item.cost = this.mitchell1Settings.labor.general.cost;
                item.pricing = this.mitchell1Settings.labor.general.pricingMatrix;
                item.displayDescOnOrder = this.mitchell1Settings.labor.displayDescOnOrder;
                item.displayHoursOnOrder = this.mitchell1Settings.labor.displayHoursOnOrder;
                item.isTaxable = this.mitchell1Settings.labor.isTaxable;
                item.qboId = this.mitchell1Settings.labor.qboId;
                item.commissionable = this.mitchell1Settings.labor.commissionable;
                item.cost = this.calculationService.roundOff(item.cost * item.count);
              }

              if (this.customFleetSettings) {
                const { isLaborRateOverride, customLaborRate } = this.customFleetSettings;

                if (isLaborRateOverride && customLaborRate) {
                  item.pricing = customLaborRate;
                  item.price = customLaborRate && customLaborRate.rate;
                  item.billRate = customLaborRate && customLaborRate.rate;
                }
              }

              if (this.mitchell1Tag && this.mitchell1Tag._id) {
                item.tags = [
                  {
                    name: this.mitchell1Tag.name,
                    color: this.mitchell1Tag.color,
                    tagId: this.mitchell1Tag._id,
                  },
                ];
              }

              item.subTotal = this.calculationService.roundOff(item.price * item.count);
              items.push(item);
            });
          }
        });
      });
    }

    if (itemDetails.fluids && itemDetails.fluids.length) {
      itemDetails.fluids.forEach((item) => {
        if (item.data && item.data.length) {
          item.data.forEach((details) => {
            const item: any = {
              _id: 'no-id',
              type: 'part',
              status: true,
              itemSource: 'Mitchell1',
              itemSourceId: sequence++,
              count: 1,
              defaultCount: 1,
              price: 0,
              cost: 0,
              isTaxable: false,
              displayDescOnOrder: false,
              displayHoursOnOrder: false,
            };

            item.title = details.name;
            item.description = `${details.item.fluidSpec} ${details.item.metricVolume} ${details.item.metricUnit}/${details.item.standardVolume} ${details.item.standardUnit}`;
            item.price = 0;

            if (this.mitchell1Settings.parts) {
              item.cost = this.calculationService.calculatingMotorPartCost(item.price, this.mitchell1Settings.parts.pricingMatrix);
              item.pricing = this.mitchell1Settings.parts.pricingMatrix;
              item.displayDescOnOrder = this.mitchell1Settings.parts.displayDescOnOrder;
              item.displayHoursOnOrder = this.mitchell1Settings.parts.displayHoursOnOrder;
              item.isTaxable = this.mitchell1Settings.parts.isTaxable;
              item.qboId = this.mitchell1Settings.parts.qboId;
              item.commissionable = this.mitchell1Settings.parts.commissionable;
            }

            if (this.customFleetSettings) {
              const { isPricingMatrixOverride, customPartPricingMatrix } = this.customFleetSettings;

              if (isPricingMatrixOverride && customPartPricingMatrix) {
                item.pricing = { pricingId: customPartPricingMatrix.pricingId, name: customPartPricingMatrix.name };
                item.cost = this.calculationService.calculatingMotorPartCost(item.price, customPartPricingMatrix);
              }
            }

            if (this.mitchell1Tag && this.mitchell1Tag._id) {
              item.tags = [
                {
                  name: this.mitchell1Tag.name,
                  color: this.mitchell1Tag.color,
                  tagId: this.mitchell1Tag._id,
                },
              ];
            }

            item.subTotal = this.calculationService.roundOff(item.price * item.count);
            items.push(item);
          });
        }
      });
    }

    let seq = new Date().getTime();
    this.items = items;
    this.items.forEach((item) => {
      if (item.type === 'labor') {
        this.handleServiceCreate({ name: item.title }, { ...item }, seq++);
      }
    });
  }

  handleSearch(query: string) {
    this.searchQuery = query;
    if (!query) {
      this.serviceSuggestions = this.helperService.deepCopy(this.services);
    } else {
      this.serviceSuggestions = this.services.filter(({ title }) => new RegExp(escapeRegExp(query), 'ig').test(title));
    }
  }

  handleServiceSelect(service: any, item: any) {
    if (!service.title) return;
    this.serviceItemMap[item.itemSourceId] = {
      service: service,
      item: item,
    };
    delete this.errors[item.itemSourceId];
  }

  handleClearService(itemSourceId: number) {
    delete this.serviceItemMap[itemSourceId];
  }

  handleAssignToAll(service: any) {
    if (service && (service._id || service.serviceTempId)) {
      this.items.forEach((item) => {
        this.handleServiceSelect(service, item);
      });
    }
  }

  handleServiceCreate({ name }, item: any, serviceTempId: number = 0) {
    const service = this.helperService.deepCopy(orderService);

    service.itemSource = 'Mitchell1';
    service.title = name;
    service.serviceTempId = serviceTempId;

    if (!serviceTempId) {
      service.serviceTempId = new Date().getTime();
    }

    this.services.push(service);
    this.handleSearch(this.searchQuery);

    this.handleServiceSelect(service, item);
  }

  handleValidation() {
    let isValid = true;

    this.items.forEach(({ itemSourceId }) => {
      if (!this.serviceItemMap[itemSourceId]) {
        this.errors[itemSourceId] = 'Service is required';
        isValid = false;
      }
    });

    return isValid;
  }

  handleSubmit() {
    if (!this.handleValidation()) return;

    const services = [];

    Object.values(this.serviceItemMap).forEach((data: any) => {
      if (data.service && data.service._id) {
        const serviceIndex = findIndex(services, { _id: data.service._id });

        if (serviceIndex === -1) {
          services.push({ _id: data.service._id, items: [data.item] });
        } else {
          services[serviceIndex].items.push(data.item);
        }
      } else if (data.service && data.service.serviceTempId) {
        const serviceIndex = findIndex(services, { serviceTempId: data.service.serviceTempId });

        if (serviceIndex === -1) {
          services.push({ ...data.service, items: [data.item] });
        } else {
          services[serviceIndex].items.push(data.item);
        }
      }
    });
    this.handleClose(true, services);
  }

  handleConfirmationOnCancel() {
    const ref = this.dialogService.open(ConfirmDismissDialogComponent, {
      width: '30%',
      styleClass: 'small-heading-dialog',
      showHeader: false,
      data: {
        styleClassHeading: 'font-14',
        title: 'Discard Imported Items',
        message: "Clicking ''Confirm'' will erase all items imported from Mitchell1. Do you want to proceed?",
        button: {
          success: {
            title: 'Confirm',
            backgroundColor: '#de6767',
          },
          cancel: { title: 'Cancel' },
        },
        headerIcon: 'fas fa-exclamation-triangle font-icon-red',
      },
    });

    const onClose = ref.onClose.subscribe(({ isSuccess, data }) => {
      if (data && data.btn === 'close') return;

      if (isSuccess) {
        this.handleClose();
      }

      onClose.unsubscribe();
    });
  }

  handleClose(isSuccess: boolean = false, data?: any) {
    this.dynamicDialogRef.close({ isSuccess, data });
  }

  isSaveButtonEnabled() {
    return this.items.length === Object.keys(this.serviceItemMap).length;
  }
}
